// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/TitleAndSubtitle';
import Spinner from '../../components/Spinner';
import PaymentMethodIcon from '../../components/PaymentMethodIcon';
import {
  ClipboardDocumentIcon,
  CheckCircleIcon,
  DocumentDuplicateIcon,
  ExclamationCircleIcon,
  LinkIcon,
  PauseIcon,
  PlayIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import { resolveMediaUrl, URL, URL_FRONT } from '../../service/Service';
import type { FormDTO, PaymentMethod } from '../../types/euforms';
import { API_KEY_HTML_PATTERN, requiresApiKey, validatePaymentConfiguration } from '../../utils/paymentConfiguration';
import { formatExpirationDate, isFormExpired } from '../../utils/formExpiration';
import SharePublicLink from '../../components/SharePublicLink';

const PAYMENT_OPTIONS: PaymentMethod[] = [
  'Numerário',
  'Multibanco',
  'MB WAY',
  'Cartão de Crédito',
  'Google Pay',
  'Apple Pay',
];

const DEFAULT_CREATE_FORM = {
  name: '',
  apiKey: '',
  info: '',
  paymentOptions: ['Numerário'] as PaymentMethod[],
};

const QUICK_TEMPLATES = [
  {
    key: 'workshop',
    title: 'Workshop / Evento',
    description: 'Ideal para inscrições locais, eventos pagos ou formações.',
    values: {
      name: 'Inscrição Workshop Local',
      info: 'Preencha os seus dados para reservar a sua inscrição.',
      paymentOptions: ['Numerário', 'Multibanco'] as PaymentMethod[],
    },
  },
  {
    key: 'donation',
    title: 'Donativo',
    description: 'Para recolher contributos simples com MB WAY, Multibanco ou numerário.',
    values: {
      name: 'Campanha de Donativos',
      info: 'Escolha o valor do seu donativo e preencha os seus dados.',
      paymentOptions: ['MB WAY', 'Multibanco'] as PaymentMethod[],
    },
  },
  {
    key: 'contact',
    title: 'Pedido de contacto',
    description: 'Útil para pedidos, contactos comerciais ou formulários sem pagamento online.',
    values: {
      name: 'Pedido de Contacto',
      info: 'Deixe os seus dados e entraremos em contacto consigo.',
      paymentOptions: ['Numerário'] as PaymentMethod[],
    },
  },
];

function getAuthHeaders(): HeadersInit {
  const token = sessionStorage.getItem('jwt');

  const headers: HeadersInit = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

async function requestJson<T>(url: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...init,
    headers: {
      ...getAuthHeaders(),
      ...(init.headers || {}),
    },
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Erro HTTP ${response.status}`);
  }

  const contentType = response.headers.get('content-type') || '';

  if (!contentType.includes('application/json')) {
    return (await response.text()) as T;
  }

  return response.json();
}

function formatDate(timestamp?: string): string {
  if (!timestamp) return 'Sem data';

  try {
    const raw = String(timestamp).trim();
    let utcDate = null;

    // Formato usado atualmente pelo euForms: dd:MM:yyyy HH:mm[:ss]
    let match = raw.match(
      /^(\d{2}):(\d{2}):(\d{4})\s+(\d{2}):(\d{2})(?::(\d{2}))?$/
    );

    if (match) {
      const [, day, month, year, hour, minute, second = '0'] = match;

      utcDate = new Date(
        Date.UTC(
          Number(year),
          Number(month) - 1,
          Number(day),
          Number(hour),
          Number(minute),
          Number(second),
        ),
      );
    }

    // Também suporta yyyy-MM-dd HH:mm[:ss]
    if (!utcDate) {
      match = raw.match(
        /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})(?::(\d{2}))?$/
      );

      if (match) {
        const [, year, month, day, hour, minute, second = '0'] = match;

        utcDate = new Date(
          Date.UTC(
            Number(year),
            Number(month) - 1,
            Number(day),
            Number(hour),
            Number(minute),
            Number(second),
          ),
        );
      }
    }

    // ISO com Z/offset, ou ISO sem timezone (tratado como UTC).
    if (!utcDate) {
      const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);
      const isoValue = hasTimezone
        ? raw
        : `${raw.replace(' ', 'T')}Z`;

      const parsed = new Date(isoValue);

      if (!Number.isNaN(parsed.getTime())) {
        utcDate = parsed;
      }
    }

    if (!utcDate || Number.isNaN(utcDate.getTime())) {
      return raw;
    }

    /*
     * A BD guarda em UTC.
     * O browser apresenta automaticamente no timezone local do utilizador.
     * Em Portugal continental:
     * - verão: UTC+1
     * - inverno: UTC
     */
    return new Intl.DateTimeFormat('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
      .format(utcDate)
      .replace(',', ' ·');
  } catch {
    return String(timestamp);
  }
}

function getInitials(name?: string): string {
  if (!name) return 'F';

  const parts = name
    .trim()
    .split(' ')
    .filter(Boolean)
    .slice(0, 2);

  return parts.map((part) => part[0]?.toUpperCase()).join('') || 'F';
}

function FormThumbnail({ form }: { form: FormDTO }) {
  const [imageUrl, setImageUrl] = useState('');
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    let active = true;
    setImageUrl('');
    setImageFailed(false);

    if (!form.logo) {
      return () => {
        active = false;
      };
    }

    fetch(`${URL}/getPresignedUrl/${form.id}`, {
      headers: { Accept: 'text/plain' },
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Erro HTTP ${response.status}`);
        return response.text();
      })
      .then((url) => {
        if (active && url) setImageUrl(resolveMediaUrl(url));
      })
      .catch(() => {
        if (active) setImageFailed(true);
      });

    return () => {
      active = false;
    };
  }, [form.id, form.logo]);

  if (!form.logo || !imageUrl || imageFailed) {
    return (
      <div className="flex h-20 w-28 flex-shrink-0 items-center justify-center rounded-2xl bg-green-cl text-base font-bold text-white shadow-sm">
        {getInitials(form.name)}
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={`Imagem do formulário ${form.name || ''}`.trim()}
      className="h-20 w-28 flex-shrink-0 rounded-2xl border border-gray-200 bg-gray-50 object-cover shadow-sm"
      onError={() => setImageFailed(true)}
    />
  );
}

function truncate(value?: string, max = 18): string {
  if (!value) return '';
  return value.length > max ? `${value.slice(0, max)}...` : value;
}

function getFormDate(form: FormDTO): string {
  if (Array.isArray(form.timestamps) && form.timestamps.length > 0) {
    return formatDate(form.timestamps[0]);
  }

  return 'Sem data';
}

export default function Forms() {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedFormId = new URLSearchParams(location.search).get('form') || '';

  const [forms, setForms] = useState<FormDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState(selectedFormId);
  const [copiedAction, setCopiedAction] = useState('');
  const [updatingStatusId, setUpdatingStatusId] = useState('');
  const [webhookConfigurations, setWebhookConfigurations] = useState<Record<string, any>>({});
  const [webhookHealthLoaded, setWebhookHealthLoaded] = useState(false);

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [formToDelete, setFormToDelete] = useState<FormDTO | null>(null);

  const [showDuplicateConfirmation, setShowDuplicateConfirmation] = useState(false);
  const [formToDuplicate, setFormToDuplicate] = useState<FormDTO | null>(null);
  const [duplicating, setDuplicating] = useState(false);
  const [duplicateSuccess, setDuplicateSuccess] = useState('');
  const [sharingForm, setSharingForm] = useState<FormDTO | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createFormData, setCreateFormData] = useState(DEFAULT_CREATE_FORM);
  const [createError, setCreateError] = useState('');
  const [creating, setCreating] = useState(false);
  const creationSuccess = Boolean(location.state?.formCreationSuccess);
  const createdFormName = location.state?.formName || 'O formulário';
  const apiKeyRequired = requiresApiKey(createFormData.paymentOptions);

  useEffect(() => {
    setSearchTerm(selectedFormId);
  }, [selectedFormId]);

  useEffect(() => {
    if (!creationSuccess) return;

    const timer = window.setTimeout(() => {
      navigate(location.pathname, { replace: true, state: {} });
    }, 8000);

    return () => window.clearTimeout(timer);
  }, [creationSuccess, location.pathname, navigate]);

  const filteredForms = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();

    if (!q) return forms;

    return forms.filter((form) => {
      const name = form.name || '';
      const id = form.id || '';
      const apiKey = form.apiKey || '';
      const methods = Array.isArray(form.paymentOptions) ? form.paymentOptions.join(' ') : '';

      return `${name} ${id} ${apiKey} ${methods}`.toLowerCase().includes(q);
    });
  }, [forms, searchTerm]);

  const stats = useMemo(() => {
    const total = forms.length;
    const active = forms.filter((form) => form.status !== 'PAUSED' && !isFormExpired(form)).length;
    const unavailable = total - active;
    const webhookIssues = forms.filter(
      (form) => webhookConfigurations[form.id]?.syncStatus === 'SYNC_REQUIRED',
    ).length;

    return { total, active, unavailable, webhookIssues };
  }, [forms, webhookConfigurations]);

  const webhookIssueForms = useMemo(
    () => forms.filter((form) => webhookConfigurations[form.id]?.syncStatus === 'SYNC_REQUIRED'),
    [forms, webhookConfigurations],
  );

  async function loadForms() {
    setLoading(true);
    setError('');
    setWebhookHealthLoaded(false);

    try {
      const [data, webhookData] = await Promise.all([
        requestJson<FormDTO[]>(`${URL}/api/forms`),
        requestJson<any[]>(`${URL}/callback/forms`).catch(() => null),
      ]);
      const normalised = Array.isArray(data) ? data : Object.values(data || {});
      const configurations = (Array.isArray(webhookData) ? webhookData : []).reduce((result, configuration) => {
        result[configuration.formId] = configuration;
        return result;
      }, {});

      setForms(normalised.filter((form) => !form.deleted).reverse());
      setWebhookConfigurations(configurations);
      setWebhookHealthLoaded(Array.isArray(webhookData));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar formulários.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadForms();
  }, []);

  function resetCreateModal() {
    setCreateFormData(DEFAULT_CREATE_FORM);
    setCreateError('');
    setCreating(false);
  }

  function closeCreateModal() {
    setShowCreateModal(false);
    resetCreateModal();
  }

  function applyTemplate(template) {
    setCreateFormData((prev) => ({
      ...prev,
      ...template.values,
      apiKey: prev.apiKey,
    }));
  }

  function togglePaymentOption(option: PaymentMethod) {
    setCreateFormData((prev) => {
      const exists = prev.paymentOptions.includes(option);

      return {
        ...prev,
        paymentOptions: exists
          ? prev.paymentOptions.filter((paymentOption) => paymentOption !== option)
          : [...prev.paymentOptions, option],
      };
    });
  }

  async function createNewForm(event: React.FormEvent) {
    event.preventDefault();
    setCreateError('');

    const name = createFormData.name.trim();
    const apiKey = createFormData.apiKey.trim();

    if (!name) {
      setCreateError('O nome do formulário é obrigatório.');
      return;
    }

    const paymentConfigurationError = validatePaymentConfiguration(
      createFormData.paymentOptions,
      apiKey,
    );

    if (paymentConfigurationError) {
      setCreateError(paymentConfigurationError);
      return;
    }

    setCreating(true);

    try {
      const createdForm = await requestJson<FormDTO>(`${URL}/api/forms`, {
        method: 'POST',
        body: JSON.stringify({
          name,
          apiKey,
          info: createFormData.info || '',
          paymentOptions: createFormData.paymentOptions,

          stock: 0,
          stockOption: 'UNLIMITED',
          maxQuantityOption: 'UNLIMITED',
          maxQuantityPerElement: 0,
          stockDecrementOption: 'PAID',

          saveReplyIp: false,
          ipMessage: '',

          latitude: 0,
          longitude: 0,
          locationUrl: '',

          sendEmailOnPaidWebhook: false,
          language: 'AUTO',
        }),
      });

      const formId = createdForm?.id;

      if (!formId) {
        throw new Error('O backend criou o formulário, mas não devolveu o identificador.');
      }

      setForms((prev) => [createdForm, ...prev]);
      closeCreateModal();
      navigate(`/forms/${formId}/formwizard/1`);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Erro ao criar formulário.');
    } finally {
      setCreating(false);
    }
  }

  async function deleteForm() {
    if (!formToDelete?.id) return;

    try {
      await requestJson<FormDTO>(`${URL}/api/forms/${formToDelete.id}`, {
        method: 'DELETE',
      });

      setForms((prev) => prev.filter((form) => form.id !== formToDelete.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao apagar formulário.');
    } finally {
      setShowDeleteConfirmation(false);
      setFormToDelete(null);
    }
  }

  function openDeleteConfirmation(form: FormDTO) {
    setFormToDelete(form);
    setShowDeleteConfirmation(true);
  }

  function openDuplicateConfirmation(form: FormDTO) {
    setError('');
    setFormToDuplicate(form);
    setShowDuplicateConfirmation(true);
  }

  function closeDuplicateConfirmation() {
    if (duplicating) return;

    setShowDuplicateConfirmation(false);
    setFormToDuplicate(null);
  }

  async function duplicateForm() {
    if (!formToDuplicate?.id || duplicating) return;

    setDuplicating(true);
    setError('');

    try {
      const duplicated = await requestJson<FormDTO>(
        `${URL}/api/forms/${formToDuplicate.id}/copy`,
        { method: 'POST' },
      );

      if (!duplicated?.id) {
        throw new Error('O backend criou a cópia, mas não devolveu o identificador.');
      }

      setForms((current) => [
        duplicated,
        ...current.filter((item) => item.id !== duplicated.id),
      ]);
      setDuplicateSuccess(
        duplicated.name || `${formToDuplicate.name || 'Formulário'} - Cópia`,
      );
      setShowDuplicateConfirmation(false);
      setFormToDuplicate(null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Não foi possível duplicar o formulário.',
      );
    } finally {
      setDuplicating(false);
    }
  }

  async function copyText(value: string, action: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedAction(action);
      window.setTimeout(() => setCopiedAction(''), 2200);
    } catch {
      setError('Não foi possível copiar para a área de transferência.');
    }
  }

  function openPublicForm(formId: string) {
    window.open(`/clientforms/${formId}`, '_blank');
  }

  async function toggleFormStatus(form: FormDTO) {
    if (!form.id || updatingStatusId) return;

    const nextStatus = form.status === 'PAUSED' ? 'ACTIVE' : 'PAUSED';
    setUpdatingStatusId(form.id);
    setError('');

    try {
      const updated = await requestJson<FormDTO>(`${URL}/api/forms/${form.id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: nextStatus }),
      });

      setForms((current) => current.map((item) => (
        item.id === form.id ? { ...item, ...updated, status: nextStatus } : item
      )));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível alterar o estado do formulário.');
    } finally {
      setUpdatingStatusId('');
    }
  }

  if (loading) return <Spinner />;

  return (
    <div className="min-h-[calc(100vh-72px)] bg-gray-50 p-5">
      {showDuplicateConfirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 px-4 py-8">
          <div className="w-full max-w-lg rounded-2xl bg-white px-6 py-6 text-left shadow-2xl">
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100">
                <DocumentDuplicateIcon className="h-6 w-6 text-emerald-700" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <h3 className="text-lg font-semibold leading-6 text-gray-900">Duplicar formulário</h3>
                <p className="mt-2 text-sm leading-6 text-gray-500">
                  Será criada uma cópia independente de{' '}
                  <strong>{formToDuplicate?.name || 'Formulário sem nome'}</strong>.
                </p>
                <div className="mt-4 rounded-xl border border-emerald-100 bg-emerald-50 p-3 text-sm text-emerald-900">
                  <p className="font-semibold">Serão copiados os campos e as configurações do formulário.</p>
                  <p className="mt-1 text-emerald-800">
                    Respostas, referências e pagamentos existentes não serão copiados.
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                onClick={closeDuplicateConfirmation}
                disabled={duplicating}
                type="button"
                className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                onClick={duplicateForm}
                disabled={duplicating}
                type="button"
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-transparent bg-green-cl px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-opacity-90 disabled:cursor-wait disabled:opacity-60"
              >
                <DocumentDuplicateIcon className="h-5 w-5" aria-hidden="true" />
                {duplicating ? 'A duplicar...' : 'Duplicar formulário'}
              </button>
            </div>
          </div>
        </div>
      )}

      <SharePublicLink open={Boolean(sharingForm)} onClose={() => setSharingForm(null)} title={sharingForm?.name || 'Formulário'} kind="formulário" url={`${URL_FRONT}/clientforms/${sharingForm?.id || ''}`} />

      {showDeleteConfirmation && (
        <div className="fixed inset-0 z-40 flex items-center justify-center overflow-y-auto bg-black/40 px-4 py-8">
          <div className="w-full max-w-lg rounded-2xl bg-white px-6 py-6 text-left shadow-2xl">
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
                <ExclamationCircleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
              </div>

              <div>
                <h3 className="text-lg font-semibold leading-6 text-gray-900">
                  Apagar formulário
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Vais apagar o formulário <strong>{formToDelete?.name || 'sem nome'}</strong>. Ele será marcado como apagado e deixará de aparecer nesta lista.
                </p>
              </div>
            </div>

            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                onClick={() => {
                  setShowDeleteConfirmation(false);
                  setFormToDelete(null);
                }}
                type="button"
                className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
              >
                Cancelar
              </button>

              <button
                onClick={deleteForm}
                type="button"
                className="rounded-lg border border-transparent bg-red-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-700"
              >
                Apagar formulário
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed bottom-0 left-0 right-0 top-[72px] z-40 flex items-center justify-center overflow-y-auto bg-black/45 px-4 py-8">
          <div className="w-full max-w-4xl overflow-hidden rounded-3xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-gray-100 px-6 py-5">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-green-cl">
                  Novo formulário
                </p>
                <h2 className="mt-1 text-2xl font-bold text-gray-900">
                  Começa com uma base simples
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Escolhe um modelo ou preenche só o essencial. Depois editas os campos no construtor visual.
                </p>
              </div>

              <button
                type="button"
                onClick={closeCreateModal}
                className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={createNewForm} className="max-h-[calc(100vh-190px)] overflow-y-auto px-6 py-6">
              <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Modelos rápidos</h3>
                  <p className="mt-1 text-xs text-gray-500">
                    Preenche automaticamente nome, descrição e pagamentos.
                  </p>

                  <div className="mt-4 space-y-3">
                    {QUICK_TEMPLATES.map((template) => (
                      <button
                        key={template.key}
                        type="button"
                        onClick={() => applyTemplate(template)}
                        className="w-full rounded-2xl border border-gray-200 bg-white p-4 text-left transition hover:border-green-cl hover:bg-gray-50"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-semibold text-gray-900">{template.title}</span>
                          <span className="rounded-full bg-green-cl px-3 py-1 text-xs font-semibold text-white">
                            Usar
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-gray-500">{template.description}</p>
                      </button>
                    ))}
                  </div>

                  <div className="mt-5 rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-4">
                    <p className="text-sm font-semibold text-gray-800">Pagamentos online opcionais</p>
                    <p className="mt-1 text-sm text-gray-500">
                      Podes criar e testar o formulário apenas com Numerário. A Chave API é necessária para Multibanco, MB WAY, Cartão de Crédito, Google Pay ou Apple Pay.
                    </p>
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700">
                      Nome do formulário *
                    </label>

                    <input
                      value={createFormData.name}
                      onChange={(event) =>
                        setCreateFormData((prev) => ({
                          ...prev,
                          name: event.target.value,
                        }))
                      }
                      placeholder="Ex.: Inscrição Workshop Local"
                      className="mt-2 block w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 outline-none focus:border-green-cl focus:ring-2 focus:ring-green-cl/20"
                      autoFocus
                    />
                  </div>

                  <div className="mt-5">
                    <label className="block text-sm font-semibold text-gray-700">
                      Chave API{apiKeyRequired ? ' *' : ' (opcional)'}
                    </label>

                    <input
                      value={createFormData.apiKey}
                      onChange={(event) =>
                        setCreateFormData((prev) => ({
                          ...prev,
                          apiKey: event.target.value,
                        }))
                      }
                      placeholder={apiKeyRequired ? 'Chave API Eupago obrigatória' : 'Necessária apenas para pagamentos online'}
                      required={apiKeyRequired}
                      pattern={API_KEY_HTML_PATTERN}
                      maxLength={24}
                      spellCheck={false}
                      title="Formato esperado: 322c-1e1b-e392-d02e-bec6"
                      className="mt-2 block w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 outline-none focus:border-green-cl focus:ring-2 focus:ring-green-cl/20"
                    />
                    <p className={`mt-2 text-xs ${apiKeyRequired ? 'font-semibold text-amber-700' : 'text-gray-500'}`}>
                      {apiKeyRequired
                        ? 'Selecionaste um método online. Formato: 322c-1e1b-e392-d02e-bec6.'
                        : 'Opcional com Numerário. Se preenchida, usa o formato 322c-1e1b-e392-d02e-bec6.'}
                    </p>
                  </div>

                  <div className="mt-5">
                    <label className="block text-sm font-semibold text-gray-700">
                      Métodos de pagamento
                    </label>

                    <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {PAYMENT_OPTIONS.map((option) => {
                        const checked = createFormData.paymentOptions.includes(option);

                        return (
                          <label
                            key={option}
                            className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition ${
                              checked ? 'border-green-cl bg-gray-50' : 'border-gray-200 hover:border-green-cl/40 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => togglePaymentOption(option)}
                              className="h-4 w-4 rounded border-gray-300 text-green-cl focus:ring-green-cl"
                            />

                            <span className="flex h-9 w-12 shrink-0 items-center justify-center rounded-lg bg-white p-1 shadow-sm ring-1 ring-gray-100">
                              <PaymentMethodIcon method={option} className="h-7 w-10" />
                            </span>

                            <span className="text-sm font-medium text-gray-700">{option}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mt-5">
                    <label className="block text-sm font-semibold text-gray-700">
                      Informação do formulário
                    </label>

                    <textarea
                      value={createFormData.info}
                      onChange={(event) =>
                        setCreateFormData((prev) => ({
                          ...prev,
                          info: event.target.value,
                        }))
                      }
                      rows={4}
                      placeholder="Texto introdutório apresentado no formulário público."
                      className="mt-2 block w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 outline-none focus:border-green-cl focus:ring-2 focus:ring-green-cl/20"
                    />
                  </div>

                  {createError && (
                    <div className="mt-5 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">
                      {createError}
                    </div>
                  )}

                  <div className="mt-6 flex flex-col gap-3 border-t border-gray-100 pt-5 sm:flex-row sm:justify-end">
                    <button
                      type="button"
                      onClick={closeCreateModal}
                      className="rounded-xl border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                    >
                      Cancelar
                    </button>

                    <button
                      disabled={creating}
                      type="submit"
                      className="rounded-xl bg-green-cl px-5 py-2.5 text-sm font-semibold text-white hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {creating ? 'A criar...' : 'Criar e abrir editor'}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
        {creationSuccess && (
          <div
            className="mb-6 flex items-start gap-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 shadow-sm"
            role="status"
          >
            <div className="flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-emerald-100">
              <CheckCircleIcon className="h-7 w-7 text-emerald-700" aria-hidden="true" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-emerald-950">Formulário criado com sucesso</p>
              <p className="mt-1 text-sm leading-6 text-emerald-800">
                “{createdFormName}” está guardado e já aparece na lista de formulários.
              </p>
            </div>
            <button
              type="button"
              aria-label="Fechar mensagem de sucesso"
              onClick={() => navigate(location.pathname, { replace: true, state: {} })}
              className="rounded-lg p-1 text-emerald-700 transition hover:bg-emerald-100"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        )}
        <div className="flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
          <Header title="Formulários" subtitle="Gerir, editar e publicar formulários" />

          <div className="flex w-full flex-col gap-3 sm:flex-row lg:w-auto">
            <button
              className="rounded-xl border border-gray-300 bg-white px-5 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              onClick={loadForms}
              type="button"
            >
              Atualizar
            </button>

            <button
              className="rounded-xl bg-green-cl px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-opacity-80"
              onClick={() => setShowCreateModal(true)}
              type="button"
            >
              Criar formulário
            </button>
          </div>
        </div>

        {webhookIssueForms.length > 0 && (
          <div className="mt-6 flex flex-col gap-4 rounded-2xl border border-amber-300 bg-amber-50 p-5 shadow-sm sm:flex-row sm:items-center">
            <div className="flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-amber-100">
              <ExclamationCircleIcon className="h-7 w-7 text-amber-700" aria-hidden="true" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-amber-950">Atenção às notificações de pagamento</p>
              <p className="mt-1 text-sm leading-6 text-amber-800">
                {webhookIssueForms.length === 1
                  ? `O formulário “${webhookIssueForms[0].name || 'Sem nome'}” deixou de conseguir validar notificações da Eupago.`
                  : `${webhookIssueForms.length} formulários deixaram de conseguir validar notificações da Eupago.`}
                {' '}Sincroniza a chave criptográfica para manter os estados dos pagamentos atualizados.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate(`/forms/${webhookIssueForms[0].id}/formwizard/2`)}
              className="rounded-xl bg-amber-700 px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-amber-800"
            >
              Resolver agora
            </button>
          </div>
        )}

        <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Total</p>
            <p className="mt-1 text-3xl font-bold text-gray-900">{stats.total}</p>
            <p className="mt-1 text-xs text-gray-500">Formulários geridos</p>
          </div>

          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Ativos</p>
            <p className="mt-1 text-3xl font-bold text-emerald-950">{stats.active}</p>
            <p className="mt-1 text-xs text-emerald-700">Disponíveis ao público</p>
          </div>

          <div className={`rounded-2xl border p-4 ${
            stats.unavailable > 0 ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-gray-50'
          }`}>
            <p className={`text-xs font-semibold uppercase tracking-wide ${
              stats.unavailable > 0 ? 'text-amber-700' : 'text-gray-500'
            }`}>Pausados ou expirados</p>
            <p className={`mt-1 text-3xl font-bold ${
              stats.unavailable > 0 ? 'text-amber-950' : 'text-gray-900'
            }`}>{stats.unavailable}</p>
            <p className={`mt-1 text-xs ${stats.unavailable > 0 ? 'text-amber-700' : 'text-gray-500'}`}>
              Indisponíveis ao público
            </p>
          </div>

          <div className={`rounded-2xl border p-4 ${
            stats.webhookIssues > 0 ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'
          }`}>
            <p className={`text-xs font-semibold uppercase tracking-wide ${
              stats.webhookIssues > 0 ? 'text-red-700' : 'text-gray-500'
            }`}>Integrações</p>
            <p className={`mt-1 text-3xl font-bold ${
              stats.webhookIssues > 0 ? 'text-red-950' : 'text-gray-900'
            }`}>{webhookHealthLoaded ? stats.webhookIssues : '—'}</p>
            <p className={`mt-1 text-xs ${stats.webhookIssues > 0 ? 'text-red-700' : 'text-gray-500'}`}>
              {webhookHealthLoaded ? 'Problemas que precisam de atenção' : 'Estado não disponível'}
            </p>
          </div>
        </div>

        {error && (
          <div className="mt-5 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">
            {error}
          </div>
        )}

        {duplicateSuccess && (
          <div
            className="mt-5 flex items-start gap-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 shadow-sm"
            role="status"
          >
            <div className="flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-emerald-100">
              <DocumentDuplicateIcon className="h-7 w-7 text-emerald-700" aria-hidden="true" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-emerald-950">Formulário duplicado com sucesso</p>
              <p className="mt-1 text-sm leading-6 text-emerald-800">
                “{duplicateSuccess}” foi criado como uma cópia independente e já aparece na lista.
              </p>
            </div>
            <button
              type="button"
              aria-label="Fechar mensagem de duplicação"
              onClick={() => setDuplicateSuccess('')}
              className="rounded-lg p-1 text-emerald-700 transition hover:bg-emerald-100"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        )}

        {selectedFormId && (
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
            <span>
              A mostrar o formulário selecionado no dashboard.
            </span>
            <button
              type="button"
              onClick={() => navigate('/forms', { replace: true })}
              className="font-bold text-emerald-800 hover:text-emerald-950"
            >
              Limpar filtro
            </button>
          </div>
        )}

        <div className="mt-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="relative w-full md:max-w-xl">
            <input
              className="block w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 pl-11 text-sm text-gray-900 shadow-sm outline-none focus:border-green-cl focus:ring-2 focus:ring-green-cl/20"
              type="text"
              placeholder="Procurar por nome, identificador, chave API ou método de pagamento"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
            <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
              ⌕
            </span>
          </div>

          <p className="text-sm text-gray-500">
            {filteredForms.length} resultado{filteredForms.length === 1 ? '' : 's'}
          </p>
        </div>
      </div>

      <div className="mt-6">
        {filteredForms.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-gray-300 bg-white p-10 text-center shadow-sm">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 text-3xl">
              +
            </div>
            <h3 className="mt-4 text-xl font-bold text-gray-900">Ainda não existem formulários</h3>
            <p className="mx-auto mt-2 max-w-md text-sm text-gray-500">
              Cria o primeiro formulário e começa com um modelo simples para o utilizador não ficar perdido no editor.
            </p>
            <button
              className="mt-6 rounded-xl bg-green-cl px-5 py-3 text-sm font-semibold text-white hover:bg-opacity-80"
              onClick={() => setShowCreateModal(true)}
              type="button"
            >
              Criar formulário
            </button>
          </div>
        ) : (
          <div className="grid gap-4 xl:grid-cols-2">
            {filteredForms.map((item) => {
              const expired = isFormExpired(item);
              const webhookConfiguration = webhookConfigurations[item.id];
              const webhookSyncRequired = webhookConfiguration?.syncStatus === 'SYNC_REQUIRED';
              return (
              <article
                key={item.id}
                className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex min-w-0 items-start gap-4">
                    <FormThumbnail form={item} />

                    <div className="min-w-0">
                      <h3 className="truncate text-lg font-bold text-gray-900">
                        {item.name || 'Formulário sem nome'}
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Criado em {getFormDate(item)}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      expired
                        ? 'bg-red-100 text-red-800'
                        : item.status === 'PAUSED'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-emerald-100 text-emerald-800'
                    }`}>
                      {expired ? 'Expirado' : item.status === 'PAUSED' ? 'Em pausa' : 'Ativo'}
                    </span>
                    <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">
                      {truncate(item.id, 12)}
                    </span>
                  </div>
                </div>

                <div className="mt-4 flex min-w-0 items-center gap-2 rounded-xl border border-emerald-100 bg-emerald-50/60 px-3 py-2">
                  <span className="flex-none text-xs font-semibold text-emerald-800">
                    Chave API
                  </span>
                  <span
                    className="min-w-0 truncate font-mono text-xs text-emerald-700"
                    title={item.apiKey || ''}
                  >
                    {item.apiKey || '—'}
                  </span>
                </div>

                {item.info && (
                  <p className="mt-4 line-clamp-2 rounded-2xl bg-gray-50 p-3 text-sm text-gray-600">
                    {item.info}
                  </p>
                )}
                {item.expirationDate && (
                  <p className={`mt-3 text-xs font-semibold ${expired ? 'text-red-700' : 'text-gray-500'}`}>
                    Disponível até {formatExpirationDate(item.expirationDate)}
                  </p>
                )}

                {webhookSyncRequired && (
                  <div className="mt-4 flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 sm:flex-row sm:items-center">
                    <ExclamationCircleIcon className="h-6 w-6 flex-none text-amber-600" aria-hidden="true" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-amber-900">Notificações de pagamento interrompidas</p>
                      <p className="mt-1 text-xs leading-5 text-amber-800">
                        A chave criptográfica pode ter sido alterada na Eupago. Sincroniza este formulário para voltar a receber estados de pagamento.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate(`/forms/${item.id}/formwizard/2`)}
                      className="rounded-xl bg-amber-700 px-4 py-2 text-xs font-bold text-white transition hover:bg-amber-800"
                    >
                      Sincronizar agora
                    </button>
                  </div>
                )}

                <div className="mt-4 flex flex-wrap gap-2">
                  {Array.isArray(item.paymentOptions) && item.paymentOptions.length > 0 ? (
                    item.paymentOptions.map((paymentOption) => (
                      <span
                        key={paymentOption}
                        className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-semibold text-gray-600"
                      >
                        {paymentOption}
                      </span>
                    ))
                  ) : (
                    <span className="rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700">
                      Sem pagamento definido
                    </span>
                  )}
                </div>

                <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                  <button
                    type="button"
                    onClick={() => navigate(`/forms/${item.id}/formwizard/1`)}
                    className="rounded-xl bg-green-cl px-3 py-2.5 text-sm font-semibold text-white hover:bg-opacity-80"
                  >
                    Editar campos
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate(`/forms/${item.id}/formwizard/2`)}
                    className="rounded-xl border border-gray-300 px-3 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Definições
                  </button>

                  <button
                    type="button"
                    onClick={() => openPublicForm(item.id)}
                    className="rounded-xl border border-gray-300 px-3 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Ver público
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate(`/forms/${item.id}/replies`)}
                    className="rounded-xl border border-gray-300 px-3 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Respostas
                  </button>
                </div>

                <div className="mt-3 flex flex-col gap-2 border-t border-gray-100 pt-3 sm:flex-row sm:items-center">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setSharingForm(item)}
                      className="inline-flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600 transition hover:bg-emerald-50 hover:text-green-cl"
                    >
                      <LinkIcon className="h-4 w-4" aria-hidden="true" />
                      Partilhar formulário
                    </button>

                    <button
                      type="button"
                      onClick={() => copyText(item.id, `id-${item.id}`)}
                      className="inline-flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600 transition hover:bg-emerald-50 hover:text-green-cl"
                    >
                      <ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" />
                      {copiedAction === `id-${item.id}` ? 'ID copiado' : 'Copiar ID'}
                    </button>

                    {item.apiKey && (
                      <button
                        type="button"
                        onClick={() => copyText(item.apiKey, `api-key-${item.id}`)}
                        className="inline-flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600 transition hover:bg-emerald-50 hover:text-green-cl"
                      >
                        <ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" />
                        {copiedAction === `api-key-${item.id}`
                          ? 'Chave API copiada'
                          : 'Copiar chave API'}
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => openDuplicateConfirmation(item)}
                      className="inline-flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600 transition hover:bg-emerald-50 hover:text-green-cl"
                    >
                      <DocumentDuplicateIcon className="h-4 w-4" aria-hidden="true" />
                      Duplicar
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={() => toggleFormStatus(item)}
                    disabled={updatingStatusId === item.id}
                    className={`inline-flex items-center gap-1.5 self-start rounded-lg px-3 py-2 text-xs font-semibold transition disabled:cursor-wait disabled:opacity-50 sm:ml-auto ${
                      item.status === 'PAUSED'
                        ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                        : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                    }`}
                  >
                    {item.status === 'PAUSED'
                      ? <PlayIcon className="h-4 w-4" aria-hidden="true" />
                      : <PauseIcon className="h-4 w-4" aria-hidden="true" />}
                    {updatingStatusId === item.id
                      ? 'A atualizar...'
                      : item.status === 'PAUSED' ? 'Ativar' : 'Pausar'}
                  </button>

                  <button
                    type="button"
                    onClick={() => openDeleteConfirmation(item)}
                    className="inline-flex items-center gap-1.5 self-start rounded-lg px-2 py-2 text-xs font-semibold text-gray-400 transition hover:bg-red-50 hover:text-red-600"
                  >
                    <TrashIcon className="h-4 w-4" aria-hidden="true" />
                    Apagar
                  </button>
                </div>
              </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
